import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorMessage, isErrorCode } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { InfoSectionContainer } from '@/common/InfoSection';
import { WBSButton } from '@/common/WBSButton';
import { Avatar } from '@/header/Avatar';
import { useCurrentUser } from '~/hooks/useCurrentUser';

interface StudyHelperInfoProps {
  studentId: number;
}

export function StudyHelperInfo({ studentId }: StudyHelperInfoProps) {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const {
    data: helper,
    error,
    isLoading,
  } = api.getStudentStudyHelper.useQuery(
    { path: { student_id: studentId } },
    // "No helper assigned" is a real answer, not a blip, so it is the one
    // failure not worth retrying; everything else gets the client's usual three.
    {
      retry: (failureCount, err) =>
        failureCount < 3 && !isErrorCode(err, 'resource_not_found'),
    },
  );

  if (isLoading) {
    return null;
  }

  // Only the 404 means "no helper assigned". A 401, a 500 or a dropped
  // connection must not be dressed as one — that is the misinformation this
  // group set out to remove, and the route's gate only closed its 403 subclass.
  if (error && !isErrorCode(error, 'resource_not_found')) {
    return (
      <StudyHelperInfoContainer>
        <AlertBox variant="error">
          {errorMessage(error, t('wbs.something-went-wrong-please'))}
        </AlertBox>
      </StudyHelperInfoContainer>
    );
  }

  if (!helper) {
    // Legacy's empty state, `buddypress/members/single/study-helpers/overview.php:70`.
    return (
      <StudyHelperInfoContainer>
        <DetailValue>{t('lessons.awaiting-adoption-by-a')}</DetailValue>
      </StudyHelperInfoContainer>
    );
  }

  const genderLabel =
    helper.gender === 'male'
      ? t('wbs.male')
      : helper.gender === 'female'
        ? t('wbs.female')
        : t('wbs.n-a-2');

  return (
    <StudyHelperInfoContainer>
      {/* Divergence §8.11: legacy links this avatar to the helper's profile
          (`overview.php:10-12`); web has no target that works. `/members/{id}/`
          branches on the VIEWER (`$memberId.index.tsx:13-20`), its non-student
          branch is mock-only (`SHProfile.tsx:13-60`), and
          `/members/{id}/profile/` resolves a Student by pk. Needs a real
          study-helper profile page — its own census row. */}
      <Avatar size={200} src={helper.avatar || null} />
      <StudyHelperDetails>
        <DetailRow>
          <DetailLabel>{t('wbs.name')}:</DetailLabel>
          <DetailValue>{helper.display_name}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{t('wbs.country')}:</DetailLabel>
          <DetailValue>{helper.country || t('wbs.n-a-2')}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{t('wbs.gender')}:</DetailLabel>
          <DetailValue>{genderLabel}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{t('wbs.age')}:</DetailLabel>
          <DetailValue>{helper.age ?? t('wbs.n-a-2')}</DetailValue>
        </DetailRow>
        {me?.id ? (
          <WBSButton
            style={{ marginTop: '23px' }}
            as={Link}
            to="/members/$memberId/messages/compose/"
            // The LOGGED-IN viewer's member id, not the student's: legacy builds
            // the href from `bp_core_get_user_domain(bp_loggedin_user_id())`
            // (`overview.php:48-50`). Latent while self was the only viewer this
            // page admitted; wrong for every other one.
            params={{ memberId: String(me.id) }}
            // Legacy's `compose/?r={helper_id}`: open compose addressed to the
            // helper, not the viewer's own inbox. `study_helpers.id` is the
            // helper's `users.id` (joined-table inheritance), which is what
            // `createThread` takes as `recipient_id`.
            search={{ r: helper.id }}
            wide
          >
            {t('lessons.message-study-helper')}
          </WBSButton>
        ) : null}
      </StudyHelperDetails>
    </StudyHelperInfoContainer>
  );
}

const StudyHelperInfoContainer = styled(InfoSectionContainer)`
  align-items: center;
  display: flex;
  gap: 40px;
  margin-bottom: 45px;
  padding: 30px 30px 39px 40px;
`;

const StudyHelperDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
`;

const DetailRow = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 8px;
`;

const DetailLabel = styled.span`
  color: var(--black);
  font-family: var(--raleway);
  font-size: 15px;
  font-weight: 600;
`;

const DetailValue = styled.span`
  color: var(--black);
  font-family: var(--raleway);
  font-size: 13px;
  font-weight: 400;
`;
